/**
 * Axios client + interceptors.
 * - Attaches Authorization header from auth store
 * - Sends X-App-Version (Q17)
 * - Sends Idempotency-Key when supplied (Q17)
 * - Normalizes backend error shape into thrown ApiError
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { use_auth_store } from './auth-store';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000/api/v1';
const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.1.0';

/**
 * Per-request opt-out from the 401 auto-logout. Set it on fetches whose 401
 * says nothing about the session — see the interceptor for why that matters.
 */
export type RequestMeta = { no_session_teardown?: boolean };

declare module 'axios' {
  interface AxiosRequestConfig extends RequestMeta {}
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly error_code: string;
  public readonly details?: Record<string, unknown>;
  public readonly trace_id?: string;
  /** Per-field messages from a 422, keyed by request field name. */
  public readonly field_errors?: Record<string, string[]>;

  constructor(opts: {
    status: number;
    error_code: string;
    message: string;
    details?: Record<string, unknown>;
    trace_id?: string;
    field_errors?: Record<string, string[]>;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.status = opts.status;
    this.error_code = opts.error_code;
    this.details = opts.details;
    this.trace_id = opts.trace_id;
    this.field_errors = opts.field_errors;
  }
}

/** Zod's flattened error payload, which the backend nests under `details.issues`. */
type ValidationIssues = {
  fieldErrors?: Record<string, string[]>;
  formErrors?: string[];
};

function read_issues(details?: Record<string, unknown>): ValidationIssues | undefined {
  return (details as { issues?: ValidationIssues } | undefined)?.issues;
}

function extract_field_errors(
  issues: ValidationIssues | undefined,
): Record<string, string[]> | undefined {
  const entries = Object.entries(issues?.fieldErrors ?? {}).filter(
    ([, msgs]) => Array.isArray(msgs) && msgs.length > 0,
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
}

/** `business_name` → `Business name`, so field names read like labels. */
function humanize_field(field: string): string {
  const words = field.replace(/[._]/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/**
 * The backend's top-level `message` for a 422 is always the generic "Request
 * validation failed" — the useful part sits in `details.issues`. Flatten it so
 * every form surfaces what actually went wrong instead of a dead-end string.
 */
function readable_validation_message(
  fallback: string,
  issues: ValidationIssues | undefined,
  field_errors: Record<string, string[]> | undefined,
): string {
  if (issues?.formErrors?.length) return issues.formErrors.join(' ');
  if (!field_errors) return fallback;
  const parts = Object.entries(field_errors).map(
    ([field, msgs]) => `${humanize_field(field)}: ${msgs[0]}`,
  );
  const shown = parts.slice(0, 3).join(' · ');
  return parts.length > 3 ? `${shown} · +${parts.length - 3} more` : shown;
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = use_auth_store.getState().access_token;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  config.headers.set('X-App-Version', APP_VERSION);
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err: AxiosError<{ error_code: string; message: string; details?: Record<string, unknown>; trace_id?: string }>) => {
    if (err.response) {
      const body = err.response.data ?? {};
      // Auto-logout on 401 — except for requests that opt out.
      //
      // A 401 is only evidence the *session* is dead when it comes from a real
      // API call. Sub-resource fetches (images, file blobs) can 401 for reasons
      // that have nothing to do with our token — a mis-scoped path, a file the
      // admin role can't read — and tearing the session down on those turned a
      // single broken adoption thumbnail into a full console logout, on every
      // page, in a loop. Those callers set `no_session_teardown` and render a
      // broken-image state instead; the next genuine API call still logs out if
      // the token really has expired.
      if (err.response.status === 401 && !(err.config as RequestMeta | undefined)?.no_session_teardown) {
        use_auth_store.getState().clear();
      }
      const issues = read_issues(body.details);
      const field_errors = extract_field_errors(issues);
      const fallback = body.message ?? err.message;
      throw new ApiError({
        status: err.response.status,
        error_code: body.error_code ?? 'UNKNOWN',
        message:
          err.response.status === 422
            ? readable_validation_message(fallback, issues, field_errors)
            : fallback,
        details: body.details,
        trace_id: body.trace_id,
        field_errors,
      });
    }
    throw new ApiError({
      status: 0,
      error_code: 'NETWORK_ERROR',
      message: err.message,
    });
  },
);
